import { useEffect, useMemo, useRef, useState } from "react";
import { formatMoneyText, moneyNumber } from "../formatting";

import {
  api,
  apiFile,
  type PortalReportPreview,
  type RefundCase,
  type ReportsOverview,
  type SubmissionPackage
} from "../api";

const reportStatusLabels: Record<string, string> = {
  ready: "Hazır",
  completed: "Tamamlandı",
  conflict: "Uyuşmazlık var",
  matched: "Eşleşti",
  pending: "Bekliyor",
  blocked: "Eksikler var",
  missing: "Hazır değil",
  draft: "Hazırlanıyor",
  review: "Kontrolde",
  queued: "Kuyrukta",
  running: "Çalışıyor",
  processing: "İşleniyor",
  succeeded: "Başarılı",
  partial: "Kısmi",
  submitted: "Gönderildi",
  failed: "Başarısız",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  resolved: "Çözüldü",
  disabled: "Devre dışı"
};

function reportStatus(value: string) {
  return reportStatusLabels[value] || value;
}

function messageText(value: ReportsOverview["portal"]["blockers"][number]) {
  if (typeof value === "string") return formatMoneyText(value);
  return formatMoneyText(value.message || value.detail || value.label || "Eksik bilgi");
}

function messageListCode(value: ReportsOverview["portal"]["blockers"][number]) {
  if (typeof value === "string") return "";
  return String(value.metadata?.list_code || "");
}

function saveBlob(blob: Blob, filename: string | null, fallback: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename || fallback;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function ReportsPage({
  cases,
  canPrepare,
  canApprove
}: {
  cases: RefundCase[];
  canPrepare: boolean;
  canApprove: boolean;
}) {
  const availableCases = useMemo(() => cases.filter((item) => !item.deleted_at), [cases]);
  const [caseId, setCaseId] = useState<number | null>(availableCases[0]?.id ?? null);
  const [overview, setOverview] = useState<ReportsOverview | null>(null);
  const [preview, setPreview] = useState<PortalReportPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const previewRef = useRef<HTMLElement | null>(null);

  const selectedCase = availableCases.find((item) => item.id === caseId) || null;

  useEffect(() => {
    if (caseId !== null && availableCases.some((item) => item.id === caseId)) return;
    setCaseId(availableCases[0]?.id ?? null);
  }, [availableCases, caseId]);

  async function load(selectedId: number) {
    setBusy(true);
    setError("");
    setPreview(null);
    try {
      setOverview(await api<ReportsOverview>(`/api/refund-cases/${selectedId}/reports`));
    } catch (reason) {
      setOverview(null);
      setError(reason instanceof Error ? reason.message : "Raporlar yüklenemedi.");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (caseId !== null) void load(caseId);
    else setOverview(null);
  }, [caseId]);

  useEffect(() => {
    if (!preview) return;
    const frame = window.requestAnimationFrame(() => {
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [preview]);

  async function showPreview(code: string) {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      setPreview(await api<PortalReportPreview>(`/api/refund-cases/${caseId}/reports/${code}/preview`));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Önizleme açılamadı.");
    } finally {
      setBusy(false);
    }
  }

  async function createPackage() {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      await api<SubmissionPackage>(`/api/refund-cases/${caseId}/submission-packages`, { method: "POST" });
      await load(caseId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Başvuru paketi hazırlanamadı.");
      setBusy(false);
    }
  }

  async function approvePackage(packageId: number) {
    if (caseId === null) return;
    setBusy(true);
    setError("");
    try {
      await api<SubmissionPackage>(`/api/submission-packages/${packageId}/approve`, {
        method: "POST",
        body: JSON.stringify({ note: "Raporlar ekranından kontrol edildi." })
      });
      await load(caseId);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Paket onaylanamadı.");
      setBusy(false);
    }
  }

  async function downloadPackage(packageId: number) {
    setBusy(true);
    setError("");
    try {
      const file = await apiFile(`/api/submission-packages/${packageId}/artifact`);
      saveBlob(file.blob, file.filename, "kdv-iade-paketi.zip");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Paket indirilemedi.");
    } finally {
      setBusy(false);
    }
  }

  if (!availableCases.length) {
    return <section className="panel"><div className="empty-state">Rapor hazırlanabilecek bir iade dosyası bulunmuyor.</div></section>;
  }

  const latestPackage = overview?.latest_package;

  return <div className="reports-page">
    <section className="panel reports-case-panel">
      <div><p className="eyebrow">RAPOR KAPSAMI</p><h2>İade dosyası seçin</h2></div>
      <label>İade dosyası<select value={caseId ?? ""} onChange={(event) => setCaseId(Number(event.target.value))}>
        {availableCases.map((item) => <option value={item.id} key={item.id}>{item.taxpayer_vkn} · {item.title} · {item.transaction_code}</option>)}
      </select></label>
      {selectedCase && <small>{selectedCase.taxpayer_title} · {selectedCase.period_start} — {selectedCase.period_end} · {selectedCase.transaction_name || selectedCase.claim_type}</small>}
    </section>

    {error && <div className="banner-error" role="alert">{error}<button onClick={() => setError("")}>×</button></div>}
    {busy && !overview && <section className="panel"><div className="empty-state">Raporlar hazırlanıyor…</div></section>}

    {overview && <>
      <section className="panel">
        <div className="panel-head"><div><p className="eyebrow">RESMÎ GİB ÇIKTILARI</p><h2>Zorunlu liste ve tablolar</h2></div><em className={`pill ${overview.portal.ready ? "active" : "partial"}`}>{overview.portal.ready ? "Hazır" : "Eksikler var"}</em></div>
        <div className="report-card-list">
          {overview.portal.outputs.map((output) => {
            const blockers = overview.portal.blockers.filter((item) => messageListCode(item) === output.code);
            return <article className="report-card" key={output.code}>
              <div><strong>{output.title}</strong><small>{output.row_count} satır · {output.upload_mode === "manual_entry" ? "Portalda manuel giriş" : `GİB ${output.extension.toUpperCase()} şablonu`}</small></div>
              <em className={`pill ${output.ready ? "active" : "partial"}`}>{output.ready ? "Hazır" : "Eksik"}</em>
              {blockers.length > 0 && <ul>{blockers.slice(0, 3).map((item, index) => <li key={index}>{messageText(item).replace(`${output.title}: `, "")}</li>)}</ul>}
              <div className="report-actions">
                <button className="text-button" disabled={busy} onClick={() => void showPreview(output.code)}>Önizle</button>
                {output.upload_mode === "excel"
                  ? <a className={`report-download ${output.ready ? "" : "disabled"}`} href={output.ready ? `/api/refund-cases/${caseId}/export/${output.code}?mode=gib` : undefined}>GİB formatında indir</a>
                  : <span>Portalda manuel girilir</span>}
              </div>
            </article>;
          })}
        </div>
      </section>

      {preview && <section className="panel report-preview" ref={previewRef}>
        <div className="panel-head"><div><p className="eyebrow">ÖNİZLEME · İLK 50 SATIR</p><h2>{preview.title}</h2></div><button className="text-button" onClick={() => setPreview(null)}>Kapat</button></div>
        {preview.issues.length > 0 && <div className="report-preview-issues">{preview.issues.slice(0, 5).map((item, index) => <span key={index}>{item}</span>)}</div>}
        <div className="report-table-wrap"><table><thead><tr>{preview.columns.map((column) => <th key={column.key}>{column.label}{column.required ? " *" : ""}</th>)}</tr></thead><tbody>
          {preview.rows.map((row, rowIndex) => <tr key={rowIndex}>{preview.columns.map((column) => <td key={column.key}>{row[column.key] !== null && row[column.key] !== undefined && row[column.key] !== "" && column.data_type === "decimal" && !/quantity|rate|ratio|weight|area|volume/.test(column.key) ? moneyNumber(String(row[column.key])) : String(row[column.key] ?? "")}</td>)}</tr>)}
        </tbody></table></div>
        {!preview.rows.length && <div className="empty-state">Bu çıktı için henüz veri bulunmuyor.</div>}
      </section>}

      <section className="panel">
        <div className="panel-head"><div><p className="eyebrow">İÇ KONTROL RAPORLARI</p><h2>Dosya kontrolleri</h2></div></div>
        <div className="report-control-grid">{overview.control_reports.map((report) => <article key={report.code}><strong>{report.title}</strong><small>{report.description}</small><em className={`pill ${report.available ? "active" : "partial"}`}>{report.available ? reportStatus(report.status) : "Hazır değil"}</em>{report.url && report.available && <a href={report.url}>XLSX indir</a>}</article>)}</div>
      </section>

      <section className="panel report-package-panel">
        <div className="panel-head"><div><p className="eyebrow">BAŞVURU PAKETİ</p><h2>Manuel GİB yükleme paketi</h2></div>{latestPackage && <em className={`pill ${latestPackage.status === "ready" ? "active" : "partial"}`}>v{latestPackage.version} · {reportStatus(latestPackage.status)}</em>}</div>
        <p>{overview.submission_capability.reason}</p>
        <div className="report-package-actions">
          {canPrepare && <button className="primary-button compact" disabled={busy} onClick={() => void createPackage()}>Paketi hazırla</button>}
          {latestPackage?.status === "ready" && <button className="secondary-button compact" disabled={busy} onClick={() => void downloadPackage(latestPackage.id)}>ZIP paketi indir</button>}
          {canApprove && latestPackage?.status === "ready" && !latestPackage.approval && <button className="secondary-button compact" disabled={busy} onClick={() => void approvePackage(latestPackage.id)}>Paketi onayla</button>}
        </div>
        <ol className="report-steps"><li>Hazır GİB Excel listelerini indirin.</li><li>Manuel giriş olarak işaretlenen tabloları portal ekranında doldurun.</li><li>Dosyaları İnternet Vergi Dairesi’ne yükleyip portal kontrollerini çalıştırın.</li><li>Portalda “Onayla” adımını kullanıcı olarak tamamlayın.</li></ol>
        <a className="text-button report-guide" href={overview.submission_capability.official_guide_url} target="_blank" rel="noreferrer">Resmî GİB kılavuzunu aç</a>
      </section>
    </>}
  </div>;
}
